<template>
  <SlHeader
    :current-step="2"
    :links="headerLinks"
  />

  <sl-loader
    v-if="isLoading"
    :message="'Chargement en cours ...'"
  />

  <div
    v-else
    data-cy="page-tarificateur-sante"
  >
    <sl-separator />

    <div class="h2 p-4">
      <div
        class="pb-4"
        data-cy="product"
      >
        SwissLife Santé Particuliers - {{ selectedCombinaison }}
      </div>
      <div class="d-flex align-items-center justify-content-between">
        <cotisation-mensuelle
          :value="currentMonthlyDue"
          class=""
        />
        <sl-button
          v-if="demarchage.consent === '1' || combinaisonChanged || !canSign"
          data-cy="action-submit"
          @click="submit()"
        >
          Enregistrer cette simulation
        </sl-button>
      </div>
    </div>

    <sl-separator />

    <notice-help />

    <sl-separator />

    <sl-pricer
      ref="slPricer"
      :refund-example-tables="refundExampleTables"
      :warranty-tables="warrantyTables"
      :current-positions-selected="currentPositionsSelected"
      :range="range"
      @cell-mouseover="onCellMouseOver($event)"
      @cell-click="onCellClick($event)"
    />

    <div
      v-if="consent === '0'"
      id="consent"
      class="p-4"
      data-cy="demarchage"
    >
      <div class="row align-items-center">
        <span>Besoin du conseil d’un expert pour trouver la meilleure solution garanties/tarif ? J’accepte qu’un conseiller Swiss Life m’appelle pour m’aider.</span>
        <demarchage-telephonique
          v-model:consent="demarchage.consent"
          v-model:telephone="demarchage.telephone"
          :server-errors="serverErrors"
        />
      </div>
    </div>

    <Transition>
      <div
        v-show="consent === '1' || demarchage.consent === '1'"
        class="p-4"
      >
        <infos-agent
          :agent="agent"
        />
      </div>
    </Transition>

    <div class="h2 p-4">
      <div class="d-flex align-items-center justify-content-between">
        <cotisation-mensuelle
          :value="currentMonthlyDue"
          class=""
        />
        <sl-button
          v-if="demarchage.consent === '1' || combinaisonChanged || !canSign"
          @click="submit()"
        >
          Enregistrer cette simulation
        </sl-button>
      </div>
    </div>

    <sl-downloads :downloads="downloads" />

    <sl-separator class="my-4" />

    <mentions-sante
      data-cy="mentions"
    />
  </div>
</template>

<script>
import { onMounted, ref, reactive, toRefs, computed, watch, nextTick } from 'vue'
import SlDownloads from 'src/modules/shared/components/SlDownloads';
import SlButton from "src/modules/shared/components/SlButton";
import SlLoader from "src/modules/shared/components/SlLoader";
import SlPricer from "src/modules/shared/components/SlPricer";
import SlSeparator from "src/modules/shared/components/SlSeparator";
import TarificationSanteService from "src/modules/tarificateur/services/tarification-sante.service";
import mockParameters from "src/modules/tarificateur/__fixtures__/getParametersResponse__sante.json";
import { formatString } from "src/modules/shared/utils/utils";
import { convertWarrantyTableResponseIntoTables} from 'src/modules/tarificateur/datatransformers/warranty-to-table.datatransformer'
import {convertRefundExampleResponseIntoTables} from 'src/modules/tarificateur/datatransformers/refund-example-to-table.datatransformer'
import CotisationMensuelle from "src/modules/tarificateur/components/CotisationMensuelle"
import DemarchageTelephonique from "src/modules/tarificateur/components/DemarchageTelephonique"
import InfosAgent from "src/modules/tarificateur/components/InfosAgent"
import {combinaisonToTablePositions, tablePositionsToCombinaison, calculateCombinaisonFromOldToAsked} from "src/modules/tarificateur/utils/combinaisonUtils";
import {formatPrice} from "src/modules/shared/utils/priceUtils";
import {SanteTypeGarantie} from "src/modules/tarificateur/model/sante-type-garantie.enum";
import {SanteFamilleCodeEnum} from "src/modules/tarificateur/model/sante-famille-code.enum";
import useDialog from "src/modules/shared/composable/useDialog";
import SlHeader from "src/modules/shared/components/SlHeader";
import NoticeHelp from "src/modules/tarificateur/components/NoticeHelp";
import MentionsSante from "src/modules/tarificateur/components/MentionsSante";
import {useVuelidate} from "@vuelidate/core";
import smoothscroll from "smoothscroll-polyfill";

export default {
  name: "TarificateurSante",
  components: {
    CotisationMensuelle,
    DemarchageTelephonique,
    InfosAgent,
    MentionsSante,
    NoticeHelp,
    SlButton,
    SlDownloads,
    SlHeader,
    SlLoader,
    SlSeparator,
    SlPricer
  },
  emits: [
    'goto'
  ],
  setup(props, {emit}) {
    const initialState = window.__INITIAL_STATE__ || {}
    smoothscroll.polyfill()
    const withElectronicSignature = ref(initialState.withElectronicSignature ?? true)
    const fromBusinessFinder = ref(initialState.fromBusinessFinder ?? false)

    const mock = false
    const content = ref('')
    const slPricer = ref(null)

    const { showLoaderSending, showPopinError, hideLoader, showPopin} = useDialog()

    // l'écart +/- autour de la sélection, à récupérer plus tard d'un ws
    const range = 8

    const parameters = ref('')
    const dateEffet = ref('')
    const originalCombinaison = ref('')
    const maxEcart = ref('')
    const numberOfLevel = ref('')

    const state = reactive({
      // ui
      isLoading: true,
      loaded: false,
      // tables
      currentPricePositionSelected: {col:3, row:0},
      currentPositionsSelected: [],
      currentMonthlyDue: '00,00 €',
      //data
      prices: new Map(),
      warrantyTables: [],
      refundExampleTables: [],
      downloads: [],
      consent: '0',
      canSign: false,
      combinaisonChanged: ''
    })

    const demarchage = reactive({
      consent: '',
      telephone: '',
    })

    const agent = reactive({
      libelleAgent: '',
      adresseAgent: '',
      telAgent: '',
      emailAgent: '',
      numOriasAgent: '',
    })

    const serverErrors = ref([])

    const headerLinks = computed(() => [
      parameters.value.devoirDeConseilUrl
    ])

    const selectedCombinaison = computed(() => {
      return tablePositionsToCombinaison(state.currentPositionsSelected)
    })

    const warrantyTablePdfUrl = computed(() => {
      return TarificationSanteService.returnWarrantyTablePdfUrl(selectedCombinaison.value)
    })

    const selectedCombinaisonWatcher = watch(
        [()=> selectedCombinaison.value],
        () => {
          state.downloads[0].link = warrantyTablePdfUrl.value
        }
    )

    const scrollToElement = (id) => {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'center'
        })
      }
    }

    const onSentQuotation = async () => {
      console.debug('[TarificateurSante]', 'onSentQuotation')
      const positions = combinaisonToTablePositions(selectedCombinaison.value)

      const data = {
        hospi: positions[SanteFamilleCodeEnum.HOSPITALISATION].col,
        optique: positions[SanteFamilleCodeEnum.OPTIQUE].col,
        medecine: positions[SanteFamilleCodeEnum.MEDECINE].col,
        dentaire: positions[SanteFamilleCodeEnum.DENTAIRE].col,
        prevention: positions[SanteFamilleCodeEnum.PREVENTION].col,
      }

      console.debug("[TarificateurSante]", "submitForm", data)

      TarificationSanteService.submitForm(data).then(res => {
        console.debug("[TarificateurSante]", "submitForm", "res", res)

        showPopin('', 'Votre demande a bien été prise en compte.', '#app')

        if (!withElectronicSignature.value || fromBusinessFinder.value) {
          emit('goto', 'souscription-sante')
        }
      }).catch(error => {
        console.error("[TarificateurVigeo]", "submit", "error", error.response.data.msg)
        const errorMsg = error.response.data.msg ?? 'Erreur serveur'
        showPopinError(errorMsg)
      }).finally(() => {
        hideLoader()
      })
    }

    const checkCurrentPricePositionAndUpdatePrice = () => {
      console.debug('[TarificateurSante]', 'checkCurrentPricePositionAndUpdatePrice', selectedCombinaison.value)

      state.currentMonthlyDue = formatPrice(state.prices.get(selectedCombinaison.value))
    }

    const onCellMouseOver = async (cell) => {
      // console.debug('[TarificateurSante]', 'onCellMouseOver', cell, selectedCombinaison.value)
      if (cell.position.col === 0) return

      const askedCombinaison = calculateCombinaisonFromOldToAsked(selectedCombinaison.value, cell, maxEcart.value)
      if (!state.prices.has(askedCombinaison)) {
        const price = await TarificationSanteService.getPrice(askedCombinaison)
        state.prices.set(askedCombinaison, price)
      }
    }

    const onCellClick = (cell) => {
      console.debug('[TarificateurSante]', 'onCellClick', cell)

      if (cell.position.col === 0) return

      const askedCombinaison = calculateCombinaisonFromOldToAsked(selectedCombinaison.value, cell, maxEcart.value)
      state.currentPositionsSelected = combinaisonToTablePositions(askedCombinaison)
      checkCurrentPricePositionAndUpdatePrice()
      if (originalCombinaison.value !== askedCombinaison) {
        state.combinaisonChanged = askedCombinaison
      } else {
        state.combinaisonChanged = ''
      }
    }

    onMounted(async () => {

      if (mock) {
        parameters.value = mockParameters
      } else {
        parameters.value = await TarificationSanteService.getParameters().then(res => res.data)
      }
      state.consent = parameters.value['consent']
      state.canSign = parameters.value['canSign']

      agent.libelleAgent = parameters.value['libelleAgent']
      agent.adresseAgent = parameters.value['adresseAgent']
      agent.villeAgent = parameters.value['villeAgent']
      agent.cpAgent = parameters.value['cpAgent']
      agent.telAgent = parameters.value['telAgent']
      agent.emailAgent = parameters.value['emailAgent']
      agent.numOriasAgent = parameters.value['numOriasAgent']

      originalCombinaison.value = parameters.value['combinaison']

      if (parameters.value['typeGarantie'] == SanteTypeGarantie.HOSPITALISATION) {
        maxEcart.value = 100

        const warrantyTable = Object.values(parameters.value.tableauGaranties)
        parameters.value.tableauGaranties = warrantyTable.filter(item => item.libelle === SanteFamilleCodeEnum.HOSPITALISATION)

        const refundExampleTable = Object.values(parameters.value.exemplesRemboursements)
        parameters.value.exemplesRemboursements = refundExampleTable.filter(item => item.libelle === SanteFamilleCodeEnum.HOSPITALISATION)

      } else {
        maxEcart.value = parameters.value['maxEcart']
      }

      numberOfLevel.value = parameters.value['maxLevel']
      state.currentMonthlyDue = formatPrice(parameters.value['price'])

      // On crée les liens de téléchargement à partir de la réponse
      const downloads = [
        {label:'Tableau de garanties détaillé', link: warrantyTablePdfUrl.value},
        {label:'Exemples de remboursements', link: parameters.value.exemplesRemboursementsPdf},
        {label:'Notice d\'information', link: parameters.value.noticeInformationsPdf}
      ]
      state.downloads = downloads

      // On convertit le retour du webservice en tableau
      const { warrantyTables } = convertWarrantyTableResponseIntoTables(parameters.value.tableauGaranties, formatString, numberOfLevel.value)
      state.warrantyTables = warrantyTables

      const { refundExampleTables } = convertRefundExampleResponseIntoTables(parameters.value.exemplesRemboursements, formatString, numberOfLevel.value)
      state.refundExampleTables = refundExampleTables

      // On se positionne sur la combinaison originale
      state.currentPositionsSelected = combinaisonToTablePositions(originalCombinaison.value)

      // On informe que tout s'est bien passé
      state.loaded = true
      state.isLoading = false

      /**
       * Récupère le "message" contenant le type de tableau à afficher par défaut et force l'affichage de celui-ci
       */
      window.addEventListener("message", function(event) {
        if(event.data.action === 'navigate') {
          // On met à jour l'iframe
          let iframe = document.querySelector('#iframe');
          iframe.src = event.data.url;
        }

        if (event.data && event.data.key === 'tableau_par_default') {
          console.debug('[TarificateurSante]', 'message', event)
          // 0 => exemple de remboursement
          // 1 => tableau de garanties
          slPricer.value.onTabClick(event.data.value === '0')
        }
      });

    })

    return {
      v$: useVuelidate(),
      onSentQuotation,
      range,
      ...toRefs(state),
      content,
      dateEffet,
      demarchage,
      agent,
      hideLoader,
      originalCombinaison,
      numberOfLevel,
      maxEcart,
      scrollToElement,
      selectedCombinaison,
      showLoaderSending,
      showPopinError,
      onCellMouseOver,
      onCellClick,
      warrantyTablePdfUrl,
      serverErrors,
      selectedCombinaisonWatcher,
      slPricer,
      headerLinks
    }
  },
  methods: {
    async submit() {

      // On reset les erreurs serveurs
      this.serverErrors = []
      await nextTick()

      // On valide les formulaires
      this.v$.$touch()
      console.debug('VUE TOUCH', this.v$)

      // Hack : On regarde si on a uniquement des erreurs serveurs car elles ne partent pas d'elles-mêmes...
      let onlyServerError = true
      for (const error of this.v$.$errors) {
        if (error.$validator !== 'serverError') onlyServerError = false
      }

      // En cas d'erreur, on ne fait rien (les composants vont afficher d'eux-mêmes les erreurs)
      if (this.v$.$error && !onlyServerError) {
        // Si le slug correspond au nom du model, le scroll s'effectue bien vers la première erreur
        this.scrollToElement(this.v$.$errors[0].$property)
        return
      }

      // Début des envois
      await this.showLoaderSending("#app")
      this.scrollToElement('sl-dialog-loader')

      if (this.consent === '0') {
        TarificationSanteService.updateConsentement(this.demarchage).then(res => {
          console.debug("[TarificationSante]", "updateConsentement", "res", res)
        }).catch(error => {
          console.error("[TarificationSante]", "submit", "error", error.response.data.msg)

          // Si on détecte des erreurs serveurs, on met la variable à jour
          if (error.response.data.violations !== undefined) {
            this.serverErrors = error.response.data.violations
            console.error(this.serverErrors)
          } else {
            const errorMsg = error.response.data.msg ?? 'Erreur serveur'
            this.showPopinError(errorMsg)
          }
        })
      }

      // Si on ne peut pas signer ou combinaison/demarchage qui change
      // On fait l'enregistrement
      if (!this.canSign || this.demarchage.consent === '1' || this.combinaisonChanged) {
        this.onSentQuotation()
        this.originalCombinaison = this.combinaisonChanged
        this.combinaisonChanged = ''
      }
    }
  }
}
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity .5s ease;
}
.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>